import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/lib/auth'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { getErrorMessage } from '@/utils/errorHandling'

export default function LoginPage() {
  const navigate = useNavigate()
  const login = useAuth((state) => state.login)
  const { t } = useTranslation(['auth', 'common'])
  const [isLoading, setIsLoading] = useState(false)

  const [formData, setFormData] = useState({
    username: '',
    password: '',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      await login(formData.username, formData.password)
      toast.success(t('auth:welcomeBack'))
      navigate('/dashboard')
    } catch (error) {
      toast.error(getErrorMessage(error))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-olive-50 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-charcoal-900 mb-2">{t('auth:welcomeBack')}</h1>
            <p className="text-olive-600">
              {t('auth:signInTo', { appName: t('common:app.name') })}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium text-charcoal-700 mb-1"
              >
                {t('auth:usernameOrEmail')}
              </label>
              <input
                id="username"
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
                className="w-full px-4 py-3 border border-olive-300 rounded-lg focus:ring-2 focus:ring-olive-500 focus:border-transparent"
                autoFocus
                dir="auto"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-charcoal-700 mb-1"
              >
                {t('auth:password')}
              </label>
              <input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="w-full px-4 py-3 border border-olive-300 rounded-lg focus:ring-2 focus:ring-olive-500 focus:border-transparent"
                dir="auto"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-olive-600 text-white py-3 rounded-lg font-semibold hover:bg-olive-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? t('common:status.loading') : t('auth:login')}
            </button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-sm text-charcoal-600 mb-4">{t('auth:dontHaveAccount')}</p>
            <Link
              to="/"
              className="inline-flex items-center justify-center px-6 py-2 border border-olive-600 rounded-lg text-olive-600 hover:bg-olive-50 transition-colors"
            >
              {t('common:nav.home')}
            </Link>
          </div>

          <div className="mt-8 pt-8 border-t border-olive-200">
            <div className="bg-olive-100 rounded-lg p-4">
              <h3 className="font-semibold text-olive-900 mb-2 text-sm">
                {t('common:crisis.benefits.title')}
              </h3>
              <ul className="text-xs text-olive-700 space-y-1">
                <li>• {t('common:crisis.benefits.rateLimit')}</li>
                <li>• {t('common:crisis.benefits.fileSize')}</li>
                <li>• {t('common:crisis.benefits.priority')}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
